// src/utils/weightSuggestions.ts
//
// ADVISORY-ONLY weight suggestions. Given an athlete's age category + division,
// surfaces the official federation weight classes so coaches/admins can spot
// them fast — but never restricts the choice. All competition weights stay
// selectable; this only re-orders + highlights.
//
// Source of truth is WEIGHT_CLASSES_BY_GROUP in rankingCalculator.ts (the same
// table used by rankings + ManageCompetitions) — we do NOT redefine weights and
// do NOT touch any category name.

import { WEIGHT_CLASSES_BY_GROUP } from "./rankingCalculator";

// User-facing category names (and the legacy raw "Pin"/"Fin" values) → the
// WEIGHT_CLASSES_BY_GROUP keys. Category names themselves are never changed.
const CATEGORY_TO_GROUP: Record<string, string> = {
  "Kids A": "Kids",  "Pin": "Kids",
  "Kids B": "Fin",   "Fin": "Fin",
  "Cadets": "Cadets", "Cadet": "Cadets",
  "Juniors": "Juniors", "Junior": "Juniors",
  "U21": "U21",
  "Seniors": "Seniors", "Senior": "Seniors",
};

/**
 * Normalizes a weight label to a sign+number key so "-58kg", "-58" and
 * " -58 kg" all compare equal. A value with no leading sign is treated as "-"
 * (handles the official Cadet-Female "41" typo → "-41"). "+57kg" → "+57".
 */
export function normalizeWeight(w: string): string {
  const str = (w ?? "").trim();
  const isPlus = str.startsWith("+");
  const digits = str.replace(/[^0-9]/g, "");
  if (!digits) return str.toLowerCase();
  return (isPlus ? "+" : "-") + digits;
}

/** Numeric ascending, "+xx" classes last — matches sorting used elsewhere. */
export function weightRank(w: string): number {
  const isPlus = (w ?? "").trim().startsWith("+");
  const n = parseInt((w ?? "").replace(/[^0-9]/g, ""), 10) || 0;
  return isPlus ? 10_000 + n : n;
}

/**
 * Official weight classes for a category + division, straight from
 * WEIGHT_CLASSES_BY_GROUP. Returns [] for an unknown/blank category — callers
 * then fall back to a flat, unhighlighted list. Purely advisory.
 */
export function getRecommendedWeights(category?: string, division?: string): string[] {
  if (!category) return [];
  const group = CATEGORY_TO_GROUP[category.trim()];
  if (!group) return [];
  const g = WEIGHT_CLASSES_BY_GROUP[group];
  if (!g) return [];
  const isFemale = String(division ?? "").toLowerCase().startsWith("f");
  return isFemale ? g.female : g.male;
}

export interface SplitWeights {
  recommended: string[];
  other: string[];
}

/**
 * Splits a competition's available weights into the recommended ones (those
 * matching the official list for the chosen category+division) and the rest.
 * Both groups come back sorted; nothing is ever removed.
 */
export function splitWeights(available: string[], recommended: string[]): SplitWeights {
  const recSet = new Set(recommended.map(normalizeWeight));
  const rec: string[] = [];
  const other: string[] = [];
  for (const w of available) {
    if (recSet.has(normalizeWeight(w))) rec.push(w);
    else other.push(w);
  }
  rec.sort((a, b) => weightRank(a) - weightRank(b));
  other.sort((a, b) => weightRank(a) - weightRank(b));
  return { recommended: rec, other };
}
